/**
 * Global license expiration detection.
 *
 * Patches `window.fetch` to intercept ALL HTTP responses (regardless of which
 * API client made the call). When a 402 with `{ errorCode: WB_EXPIRED_LICENSE }`
 * is detected, a DOM event is emitted so `LicenseGuard` can react immediately.
 *
 * The patched fetch returns a never-resolving Promise to prevent callers from
 * processing the error (which would cause flash-of-error before the expired page).
 */

import { PlatformErrorCode } from '@agilelab/plugin-wb-platform-common';

export const LICENSE_EXPIRED_EVENT = 'witboost:license-expired';

/** Global flag — can be read synchronously during render as a safety net. */
export let licenseExpiredFlag = false;

export function emitLicenseExpired(): void {
  licenseExpiredFlag = true;
  window.dispatchEvent(new Event(LICENSE_EXPIRED_EVENT));
}

export function installLicenseExpiredInterceptor(): void {
  const originalFetch = window.fetch.bind(window);

  window.fetch = async (
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> => {
    const response = await originalFetch(input, init);

    if (response.status === 402) {
      const cloned = response.clone();
      try {
        const body = await cloned.json();
        if (body?.errorCode === PlatformErrorCode.WB_EXPIRED_LICENSE) {
          emitLicenseExpired();
          // Never resolve — prevents the caller from processing the error
          return new Promise<Response>(() => {});
        }
      } catch {
        // Not JSON — regular 402, pass through
      }
    }

    return response;
  };
}
